import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import { HeaderProps } from '../../components/Layout/Header'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { formatPipe, transformDate } from '../../utils/utils'
import Loading from '../../components/Display/Loading'
import useSnackBars from '../../hooks/SnackbarConsumerHook'
import { ThemeContext } from '../../context/ThemeContext'
import {
  BsPersonFill,
  BsGraphUp,
  BsGraphDown,
  BsCashCoin,
} from 'react-icons/bs'
import { IoTrendingUp, IoTrendingDown, IoCalendar } from 'react-icons/io5'
import { MdAttachMoney, MdPeople } from 'react-icons/md'
import { FaChartLine } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'

const server = process.env.REACT_APP_SERVER_URL

// Interfaces TypeScript
interface AnalyticsResponse {
  kpis: {
    totalActiveSubscribers: number
    monthlyRevenue: number
    usdMonthlyRevenue: number
    mrr: number
    newThisMonth: number
    canceledThisMonth: number
    churnRate: number
    growthRate: number
  }
  upcomingExpirations: Array<{
    clientId: string
    name: string
    subscriptionId: string
    description: string
    endDate: string
    daysLeft: number
  }>
  projections: {
    perMonth: Array<{ month: string; expectedRevenue: number }>
    horizon3: number
    horizon6: number
    horizon12: number
  }
  plansComparison: Array<{
    subscriptionId: string
    description: string
    activeSubscribers: number
    mrr: number
    avgTicket: number
    growthRate: number
  }>
  statuses: Array<{
    derivedStatus: 'Activo' | 'Pendiente' | 'Vencido' | 'Suspendido' | 'Cancelado'
    count: number
  }>
  trends: {
    last12: Array<{
      month: string
      growthCount: number
      churnCount: number
      growthRate: number
      churnRate: number
    }>
  }
}

const SubscriptionAnalytics: React.FC<SubscriptionAnalyticsProps> = ({
  setHeaderActions,
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const { addAlert } = useSnackBars()
  const { theme } = useContext(ThemeContext)
  const user = useSelector((state: RootState) => state?.auth?.user)

  // Verificar si el usuario tiene plan pyme, emprendedor o estudiantil
  const userPlan = user?.['custom:user_plan']
  const isNonCorporateUser = userPlan === 'pyme' || userPlan === 'emprendedor' || userPlan === 'estudiantil'

  useEffect(() => {
    setHeaderActions({})
    return () => {
      setHeaderActions(null)
    }
  }, [setHeaderActions])

  const getAnalytics = async () => {
    setLoading(true)
    try {
      const response = await axios.get<AnalyticsResponse>(
        `${server}/subscription/analytics`
      )
      setAnalytics(response.data)
    } catch (error) {
      addAlert({
        message: 'Error al cargar las estadísticas',
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // No cargar datos si el usuario no tiene plan corporativo
    if (!isNonCorporateUser) {
      getAnalytics()
    }
  }, [isNonCorporateUser])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading />
      </div>
    )
  }

  // Mostrar mensaje de upgrade si no es plan corporativo
  if (isNonCorporateUser) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-4">
        <div
          className="p-8 rounded-lg shadow-lg max-w-2xl text-center"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="mb-6">
            <BsGraphUp className="mx-auto mb-4" size={64} style={{ color: '#507CDC' }} />
          </div>
          <h2 className="text-2xl font-bold mb-4 dark:text-white">
            Funcionalidad Premium
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Para ver esta pantalla debes mejorar tu plan a <strong className="text-primary">"Corporativo"</strong>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            El plan Corporativo incluye estadísticas avanzadas, análisis de tendencias, proyecciones de ingresos y mucho más.
          </p>
        </div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-gray-500 dark:text-gray-400">
          No hay datos disponibles
        </p>
      </div>
    )
  }

  const colors = {
    primary: '#507CDC',
    primaryDark: '#062F97',
    blue1: '#1400FF',
    blue2: '#0033A0',
    green: '#31DA16',
    red: '#FF0000',
    orange: '#FF6B00',
    yellow: '#FFC107',
  }

  const statusColors = {
    Activo: '#31DA16',
    Pendiente: '#FFC107',
    Vencido: '#FF0000',
    Suspendido: '#FF6B00',
    Cancelado: '#B81919',
  }

  return (
    <div className="w-full h-full overflow-auto">
      <h2 className="text-2xl font-bold mb-6 dark:text-white">
        Estadísticas de Suscripciones
      </h2>

      {/* KPIs Section - Grid de 4 columnas */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        {/* Total Active Subscribers */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.primary }}
            >
              <BsPersonFill className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Suscriptores Activos
          </p>
          <h3 className="text-3xl font-bold text-primary">
            {analytics.kpis.totalActiveSubscribers}
          </h3>
        </div>

        {/* Monthly Revenue Bs */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.primaryDark }}
            >
              <BsCashCoin className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Ingresos Mensuales Bs
          </p>
          <h3 className="text-3xl font-bold text-primary">
            {formatPipe(analytics.kpis.monthlyRevenue + '')}
          </h3>
        </div>

        {/* Monthly Revenue USD */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.blue1 }}
            >
              <MdAttachMoney className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Ingresos Mensuales USD
          </p>
          <h3 className="text-3xl font-bold text-primary">
            ${formatPipe(analytics.kpis.usdMonthlyRevenue + '')}
          </h3>
        </div>

        {/* MRR */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.blue2 }}
            >
              <FaChartLine className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            MRR (Monthly Recurring Revenue)
          </p>
          <h3 className="text-3xl font-bold text-primary">
            ${formatPipe(analytics.kpis.mrr + '')}
          </h3>
        </div>

        {/* New This Month */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.green }}
            >
              <IoTrendingUp className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Nuevos este mes
          </p>
          <h3 className="text-3xl font-bold" style={{ color: colors.green }}>
            {analytics.kpis.newThisMonth}
          </h3>
        </div>

        {/* Canceled This Month */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.red }}
            >
              <IoTrendingDown className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Cancelados este mes
          </p>
          <h3 className="text-3xl font-bold" style={{ color: colors.red }}>
            {analytics.kpis.canceledThisMonth}
          </h3>
        </div>

        {/* Churn Rate */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.orange }}
            >
              <BsGraphDown className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Tasa de Churn
          </p>
          <h3 className="text-3xl font-bold" style={{ color: colors.orange }}>
            {(analytics.kpis.churnRate * 100).toFixed(2)}%
          </h3>
        </div>

        {/* Growth Rate */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: colors.green }}
            >
              <BsGraphUp className="text-white" size={24} />
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Tasa de Crecimiento
          </p>
          <h3 className="text-3xl font-bold" style={{ color: colors.green }}>
            {(analytics.kpis.growthRate * 100).toFixed(2)}%
          </h3>
        </div>
      </div>

      {/* Horizons Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#F6F7FC',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center mb-3">
            <IoCalendar className="text-primary mr-2" size={20} />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Proyección 3 meses
            </p>
          </div>
          <h3 className="text-2xl font-bold text-primary">
            ${formatPipe(analytics.projections.horizon3 + '')}
          </h3>
        </div>

        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#F6F7FC',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center mb-3">
            <IoCalendar className="text-primary mr-2" size={20} />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Proyección 6 meses
            </p>
          </div>
          <h3 className="text-2xl font-bold text-primary">
            ${formatPipe(analytics.projections.horizon6 + '')}
          </h3>
        </div>

        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#F6F7FC',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="flex items-center mb-3">
            <IoCalendar className="text-primary mr-2" size={20} />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Proyección 12 meses
            </p>
          </div>
          <h3 className="text-2xl font-bold text-primary">
            ${formatPipe(analytics.projections.horizon12 + '')}
          </h3>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        {/* Projections Chart */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <h3 className="text-lg font-semibold mb-4 dark:text-white">
            Proyección de Ingresos (Próximos 12 meses)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.projections.perMonth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                stroke={theme === 'dark' ? '#fff' : '#000'}
              />
              <YAxis stroke={theme === 'dark' ? '#fff' : '#000'} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="expectedRevenue"
                stroke={colors.primary}
                strokeWidth={2}
                name="Ingresos Proyectados"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Trends Chart */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <h3 className="text-lg font-semibold mb-4 dark:text-white">
            Tendencias (Últimos 12 meses)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.trends.last12}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                stroke={theme === 'dark' ? '#fff' : '#000'}
              />
              <YAxis stroke={theme === 'dark' ? '#fff' : '#000'} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="growthRate"
                stroke={colors.green}
                strokeWidth={2}
                name="Tasa de Crecimiento"
              />
              <Line
                type="monotone"
                dataKey="churnRate"
                stroke={colors.red}
                strokeWidth={2}
                name="Tasa de Churn"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution Chart */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <h3 className="text-lg font-semibold mb-4 dark:text-white">
            Distribución por Estado
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.statuses}
                dataKey="count"
                nameKey="derivedStatus"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {analytics.statuses.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={statusColors[entry.derivedStatus]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Upcoming Expirations Table */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <h3 className="text-lg font-semibold mb-4 dark:text-white">
            Próximas Expiraciones
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Cliente
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Suscripción
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Fecha de Vencimiento
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Días Restantes
                  </th>
                </tr>
              </thead>
              <tbody>
                {analytics.upcomingExpirations.map((expiration, index) => {
                  const daysColor =
                    expiration.daysLeft < 15
                      ? colors.red
                      : expiration.daysLeft < 30
                      ? colors.yellow
                      : colors.green
                  return (
                    <tr
                      key={index}
                      className="border-b border-gray-100 dark:border-gray-800"
                    >
                      <td className="py-3 px-4 text-sm dark:text-gray-300">
                        {expiration.name}
                      </td>
                      <td className="py-3 px-4 text-sm dark:text-gray-300">
                        {expiration.description}
                      </td>
                      <td className="py-3 px-4 text-sm dark:text-gray-300">
                        {transformDate(expiration.endDate)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span
                          className="font-semibold"
                          style={{ color: daysColor }}
                        >
                          {expiration.daysLeft} días
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Plans Comparison Table */}
        <div
          className="p-6 rounded-lg shadow-lg"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <h3 className="text-lg font-semibold mb-4 dark:text-white">
            Comparación de Planes
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Plan
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Suscriptores Activos
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    MRR
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Ticket Promedio
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">
                    Tasa de Crecimiento
                  </th>
                </tr>
              </thead>
              <tbody>
                {analytics.plansComparison.map((plan, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-3 px-4 text-sm dark:text-gray-300">
                      {plan.description}
                    </td>
                    <td className="py-3 px-4 text-sm dark:text-gray-300">
                      {plan.activeSubscribers}
                    </td>
                    <td className="py-3 px-4 text-sm dark:text-gray-300">
                      ${formatPipe(plan.mrr + '')}
                    </td>
                    <td className="py-3 px-4 text-sm dark:text-gray-300">
                      ${formatPipe(plan.avgTicket + '')}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span
                        className="font-semibold"
                        style={{
                          color: plan.growthRate >= 0 ? colors.green : colors.red,
                        }}
                      >
                        {(plan.growthRate * 100).toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionAnalytics

interface SubscriptionAnalyticsProps {
  setHeaderActions: React.Dispatch<
    React.SetStateAction<HeaderProps['headerActions'] | null | undefined>
  >
}
